import React, { useEffect, useState } from "react";
import {
  Comparators,
  EuiBasicTable,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiHeader,
  EuiHeaderLink,
  EuiHeaderLinks,
  EuiHeaderLogo,
  EuiHeaderSectionItem,
  EuiLoadingChart,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { db } from "./firebase/firebase";
import { format } from "date-fns";

import {
  Page,
  Text,
  Document,
  StyleSheet,
  PDFViewer,
  Font,
  Image,
} from "@react-pdf/renderer";
import { useHistory } from "react-router-dom";

Font.register({
  family: "Oswald",
  src: "https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf",
});

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  title: {
    fontSize: 24,
    textAlign: "center",
    fontFamily: "Oswald",
  },
  author: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 40,
  },
  subtitle: {
    fontSize: 18,
    margin: 12,
    fontFamily: "Oswald",
  },
  text: {
    margin: 12,
    fontSize: 14,
    textAlign: "justify",
    fontFamily: "Times-Roman",
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: "center",
    color: "grey",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
});

export const ApplicationSubmissions: React.FC = () => {
  const history = useHistory();

  const [applications, loading] = useCollectionData(
    db().collection("tbdf-applications-2026").orderBy("timestamp", "asc"),
  );

  useEffect(() => {
    console.log(applications);
  }, [loading, applications]);

  const [currentApplication, setCurrentApplication] = useState<any>(null);
  const [isFlyoutVisible, setFlyoutVisible] = useState<boolean>(false);
  const [isLoadingPDF, setLoadingPDF] = useState<boolean>(true);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(6);

  const some = (
    pageIndex: number,
    pageSize: number,
    sortField?: any,
    sortDirection?: any,
  ) => {
    let items: any = [];

    if (sortField) {
      items = (applications || [])
        .slice(0)
        .sort(
          Comparators.property(sortField, Comparators.default(sortDirection)),
        );
    } else {
      items = applications;
    }

    let pageOfItems;

    if (!pageIndex && !pageSize) {
      pageOfItems = items;
    } else {
      const startIndex = pageIndex * pageSize;
      pageOfItems = items?.slice(
        startIndex,
        Math.min(startIndex + pageSize, items.length),
      );
    }

    return {
      pageOfItems,
      totalItemCount: items?.length,
    };
  };

  const { pageOfItems, totalItemCount } = some(pageIndex, pageSize);

  const pagination = {
    pageIndex: pageIndex,
    pageSize: pageSize,
    totalItemCount: totalItemCount || 0,
    pageSizeOptions: [5, 8, 16, 20],
  };

  const onTableChange = ({ page = {} as any }) => {
    const { index: pageIndex, size: pageSize } = page;

    setPageIndex(pageIndex);
    setPageSize(pageSize);
  };

  const columns = [
    {
      field: "innovatorName",
      name: "Innovator Name",
      sortable: true,
    },
    {
      field: "contact",
      name: "Contact Number",
      sortable: true,
    },
    {
      field: "email",
      name: "Email",
      sortable: true,
    },
    {
      field: "name",
      name: "Project Name",
    },
    {
      field: "timestamp",
      name: "Date Submitted",
      render: (date: any) => {
        return `${format(date.toDate(), "EEE do MMM yyyy, h:mm:ss")}`;
      },
    },
  ];

  const getRowProps = (item: any) => {
    const { id } = item;
    return {
      "data-test-subj": `row-${id}`,
      className: "customRowClass",
      onClick: () => {
        setCurrentApplication(item);
        setFlyoutVisible(true);
        setLoadingPDF(true);
      },
    };
  };

  const logout = () => {
    console.log("SOME");

    history.push("/admin/login");
  };

  return (
    <div>
      <EuiHeader>
        <EuiHeaderSectionItem border="right">
          <EuiHeaderLogo>Technology Business Development Fund</EuiHeaderLogo>
        </EuiHeaderSectionItem>

        <EuiHeaderSectionItem>
          <EuiHeaderLinks>
            <EuiHeaderLink iconType="exit" onClick={() => logout()}>
              Logout
            </EuiHeaderLink>
          </EuiHeaderLinks>
        </EuiHeaderSectionItem>
      </EuiHeader>
      <div style={{ margin: 24, height: "100vh" }}>
        <EuiText textAlign="center">
          <h2>Technology Business Development Fund</h2>
          <h3 style={{ marginTop: 0 }}>
            Application Submissions {totalItemCount || "..."}
          </h3>
        </EuiText>
        <EuiSpacer size="l" />
        <EuiSpacer size="l" />
        <EuiPanel>
          <EuiBasicTable
            items={(pageOfItems as any[]) ?? []}
            loading={loading}
            rowHeader="firstName"
            columns={columns}
            pagination={pagination}
            rowProps={getRowProps}
            onChange={onTableChange}
          />
        </EuiPanel>

        {isFlyoutVisible && (
          <EuiFlyout
            ownFocus
            onClose={() => setFlyoutVisible(false)}
            aria-labelledby="flyoutTitle"
          >
            <EuiFlyoutHeader hasBorder>
              <EuiTitle size="m">
                <h2 id="flyoutTitle">Application Details</h2>
              </EuiTitle>
            </EuiFlyoutHeader>
            <EuiFlyoutBody>
              {isLoadingPDF && (
                <EuiText textAlign="center">
                  <EuiLoadingChart size="xl" mono />
                  <h3>Loading document...</h3>
                </EuiText>
              )}
              <div>
                {currentApplication && (
                  <PDFViewer
                    style={{
                      position: "absolute",
                      border: 0,
                      height: "100%",
                      width: "95%",
                    }}
                  >
                    <Document onRender={() => setLoadingPDF(false)}>
                      <Page style={styles.body}>
                        <Text style={styles.title}>
                          {currentApplication?.name}
                        </Text>
                        <Text style={styles.author}>
                          {currentApplication?.innovatorName}
                        </Text>
                        {!isBlank(currentApplication?.imageUrl) &&
                          !isEmpty(currentApplication?.imageUrl) && (
                            <Image
                              style={styles.image}
                              src={currentApplication.imageUrl}
                            />
                          )}
                        <Text style={styles.subtitle}>Personal Details</Text>
                        <Text style={styles.text}>
                          Full name: {currentApplication.innovatorName}
                        </Text>
                        <Text style={styles.text}>
                          Email: {currentApplication.email}
                        </Text>
                        <Text style={styles.text}>
                          Contact Number: {currentApplication.contact}
                        </Text>
                        <Text style={styles.text}>
                          Alternative Contact: {currentApplication.altContact}
                        </Text>
                        <Text style={styles.text}>
                          NRC Number: {currentApplication.nrcNumber}
                        </Text>
                        <Text style={styles.subtitle}>
                          Brief Description of Innovation
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.description}
                        </Text>
                        <Text style={styles.subtitle}>
                          Purpose of Application
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.purpose}
                        </Text>
                        <Text style={styles.subtitle}>
                          Commercial Viability of Innovation
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.viability}
                        </Text>
                        <Text style={styles.subtitle}>
                          Stage of the Project
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.stage}
                        </Text>
                        <Text style={styles.subtitle}>
                          Technical Feasibility
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.feasibility}
                        </Text>
                        <Text style={styles.subtitle}>
                          Anticipated Social Impact
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.socialImpact}
                        </Text>
                        <Text style={styles.subtitle}>Applied Amount</Text>
                        <Text style={styles.text}>
                          ZMW {currentApplication.appliedAmount}
                        </Text>
                        <Text style={styles.subtitle}>Total Cost</Text>
                        <Text style={styles.text}>
                          ZMW {currentApplication.totalCost}
                        </Text>
                        <Text style={styles.subtitle}>Category</Text>
                        <Text style={styles.text}>
                          {currentApplication.category}
                        </Text>
                        <Text style={styles.subtitle}>Proof of Funds</Text>
                        <Text style={styles.text}>
                          {currentApplication.fundsProof}
                        </Text>
                        <Text style={styles.subtitle}>Partnerships</Text>
                        <Text style={styles.text}>
                          {currentApplication.partnerships}
                        </Text>
                        <Text style={styles.subtitle}>Risk Management</Text>
                        <Text style={styles.text}>
                          {currentApplication?.risks}
                        </Text>
                        <Text style={styles.subtitle}>Team Compositions</Text>
                        <Text style={styles.text}>
                          {currentApplication.teamComposition}
                        </Text>
                        <Text style={styles.subtitle}>
                          Innovativeness of the Project
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.innovation}
                        </Text>
                        <Text style={styles.subtitle}>
                          Usefulness of the Project
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.usefulness}
                        </Text>
                        <Text style={styles.subtitle}>
                          Viability of the Project
                        </Text>
                        <Text style={styles.text}>
                          {currentApplication.viability}
                        </Text>
                        <Text style={styles.subtitle}>
                          National Registration Card
                        </Text>
                        {!isBlank(currentApplication?.nrcUrl) &&
                          !isEmpty(currentApplication?.nrcUrl) && (
                            <Image
                              style={styles.image}
                              src={currentApplication.nrcUrl}
                            />
                          )}
                        <Text style={styles.subtitle}>
                          Certificate of Registration
                        </Text>
                        {!isBlank(currentApplication?.regUrl) &&
                          !isEmpty(currentApplication?.regUrl) && (
                            <Image
                              style={styles.image}
                              src={currentApplication.regUrl}
                            />
                          )}
                        <Text
                          style={styles.pageNumber}
                          render={({ pageNumber, totalPages }) =>
                            `${pageNumber} / ${totalPages}`
                          }
                          fixed
                        />
                      </Page>
                    </Document>
                  </PDFViewer>
                )}
              </div>
            </EuiFlyoutBody>
          </EuiFlyout>
        )}
      </div>
    </div>
  );
};

function isBlank(str: string) {
  return !str || /^\s*$/.test(str);
}

function isEmpty(str: string) {
  return !str || str.length === 0;
}
